import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, MenuItem, Checkbox, FormControl, InputLabel, Select, OutlinedInput, CircularProgress, Chip, ListItemText, Popover, List, ListItemButton, Alert, useMediaQuery, useTheme, Card, CardContent, IconButton, TableSortLabel } from '@mui/material';
import { Refresh as RefreshIcon, CalendarMonth as CalendarIcon, MoreVert as MoreVertIcon, ViewColumn as ColumnsIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { UAParser } from 'ua-parser-js';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const datePresets = [
  { label: 'Today', getValue: () => { const t = new Date(); return { start: t, end: t }; } },
  { label: 'Yesterday', getValue: () => { const t = new Date(); t.setDate(t.getDate() - 1); return { start: t, end: t }; } },
  { label: 'Last 7 Days', getValue: () => { const t = new Date(); const start = new Date(t); start.setDate(t.getDate() - 6); return { start, end: t }; } },
  { label: 'Last 30 Days', getValue: () => { const t = new Date(); const start = new Date(t); start.setDate(t.getDate() - 29); return { start, end: t }; } },
  { label: 'This Month', getValue: () => { const t = new Date(); const start = new Date(t.getFullYear(), t.getMonth(), 1); return { start, end: t }; } },
  { label: 'Last Month', getValue: () => { const t = new Date(); const start = new Date(t.getFullYear(), t.getMonth() - 1, 1); const end = new Date(t.getFullYear(), t.getMonth(), 0); return { start, end }; } },
];

const statusColors = { approved: 'success', pending: 'warning', rejected: 'error', invalid: 'default' };

const allColumns = [
  { id: 'status', label: 'Status', group: 'Basic' },
  { id: 'created_at', label: 'Date', group: 'Basic' },
  { id: 'click_date', label: 'Click Date', group: 'Basic' },
  { id: 'campaign_name', label: 'Offer', group: 'Basic' },
  { id: 'partner_name', label: 'Partner', group: 'Basic' },
  { id: 'revenue', label: 'Revenue', group: 'Financial' },
  { id: 'payout', label: 'Payout', group: 'Financial' },
  { id: 'conversion_ip', label: 'Conv. IP', group: 'Technical' },
  { id: 'session_ip', label: 'Session IP', group: 'Technical' },
  { id: 'click_id', label: 'Click ID', group: 'Technical' },
  { id: 'user_agent', label: 'User Agent', group: 'Technical' },
  { id: 'browser', label: 'Browser', group: 'Device' },
  { id: 'device', label: 'Device', group: 'Device' },
  { id: 'os', label: 'OS', group: 'Device' },
  { id: 'source_id', label: 'Source', group: 'Tracking' },
  { id: 'creative_id', label: 'Creative', group: 'Tracking' },
  { id: 'offer_url_id', label: 'Offer URL', group: 'Tracking' },
  { id: 'event_name', label: 'Event', group: 'Tracking' },
  { id: 'attribution', label: 'Attribution', group: 'Tracking' },
];

// All columns except user_agent
const defaultColumns = allColumns.filter(c => c.id !== 'user_agent').map(c => c.id);

function Conversions() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isPartner } = useAuth();
  // Restore state from URL params
const getInitialDate = (param, fallback) => {
    const val = searchParams.get(param);
    if (!val) return fallback;
    // Parse date string as local date, not UTC
    const [year, month, day] = val.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [pendingLeads, setPendingLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [startDate, setStartDate] = useState(getInitialDate('startDate', new Date()));
  const [endDate, setEndDate] = useState(getInitialDate('endDate', new Date()));
  const [filters, setFilters] = useState({ 
  status: searchParams.get('status') || 'approved',
    campaigns: searchParams.get('campaigns')?.split(',').filter(Boolean).map(Number) || [], 
    partners: searchParams.get('partners')?.split(',').filter(Boolean).map(Number) || [], 
    advertisers: searchParams.get('advertisers')?.split(',').filter(Boolean).map(Number) || [] 
  });
  const [dateAnchor, setDateAnchor] = useState(null);
  const [colAnchor, setColAnchor] = useState(null);
  const [searchCampaign, setSearchCampaign] = useState('');
  const [searchPartner, setSearchPartner] = useState('');
  const [searchAdvertiser, setSearchAdvertiser] = useState('');
  const [visibleColumns, setVisibleColumns] = useState(defaultColumns);
  const [orderBy, setOrderBy] = useState(searchParams.get('orderBy') || 'created_at');
  const [orderDir, setOrderDir] = useState(searchParams.get('orderDir') || 'desc');
  const [reportRan, setReportRan] = useState(searchParams.get('ran') === 'true');

  useEffect(() => { 
    loadFilters(); 
    if (!isPartner()) loadPendingLeads();
    // If we have URL params indicating a previous report, reload it
    if (searchParams.get('ran') === 'true') {
      loadLeads();
    }
  }, []);

  // Update URL when filters change
  const updateUrlParams = () => {
    const params = new URLSearchParams();
    params.set('startDate', `${startDate.getFullYear()}-${String(startDate.getMonth()+1).padStart(2,'0')}-${String(startDate.getDate()).padStart(2,'0')}`);
    params.set('endDate', `${endDate.getFullYear()}-${String(endDate.getMonth()+1).padStart(2,'0')}-${String(endDate.getDate()).padStart(2,'0')}`);
    params.set('status', filters.status);
    params.set('orderBy', orderBy);
    params.set('orderDir', orderDir);
    if (filters.campaigns.length) params.set('campaigns', filters.campaigns.join(','));
    if (filters.partners.length) params.set('partners', filters.partners.join(','));
    if (filters.advertisers.length) params.set('advertisers', filters.advertisers.join(','));
    if (reportRan) params.set('ran', 'true');
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    if (reportRan) updateUrlParams();
  }, [startDate, endDate, filters, orderBy, orderDir, reportRan]);

  const loadFilters = async () => {
    try {
      const [campRes, partRes, advRes] = await Promise.all([
        axios.get('/admin/campaigns'),
        axios.get('/admin/partners'),
        axios.get('/admin/advertisers').catch(() => ({ data: [] }))
      ]);
      setCampaigns(Array.isArray(campRes.data) ? campRes.data : campRes.data?.data || []);
      setPartners(Array.isArray(partRes.data) ? partRes.data : partRes.data?.data || []);
      setAdvertisers(Array.isArray(advRes.data) ? advRes.data : advRes.data?.data || []);
    } catch (e) { console.error(e); }
  };

  const loadPendingLeads = async () => {
    try {
      const params = new URLSearchParams({ status: 'pending' });
      const res = await axios.get('/admin/leads?' + params);
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setPendingLeads(parseLeads(data));
    } catch (e) { console.error(e); }
  };

  const parseLeads = (data) => {
    return data.map(lead => {
      const parser = new UAParser(lead.user_agent || lead.conversion_ua || '');
      const result = parser.getResult();
      return {
        ...lead,
        browser: result.browser.name || '-',
        device: result.device.type || 'desktop',
        os: result.os.name ? `${result.os.name} ${result.os.version || ''}`.trim() : '-',
      };
    });
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
       startDate: `${startDate.getFullYear()}-${String(startDate.getMonth()+1).padStart(2,'0')}-${String(startDate.getDate()).padStart(2,'0')}`, 
        endDate: `${endDate.getFullYear()}-${String(endDate.getMonth()+1).padStart(2,'0')}-${String(endDate.getDate()).padStart(2,'0')}`
      });
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.campaigns.length) params.append('campaigns', filters.campaigns.join(','));
      if (filters.partners.length) params.append('partners', filters.partners.join(','));
      if (filters.advertisers.length) params.append('advertisers', filters.advertisers.join(','));
      const res = await axios.get('/admin/leads?' + params);
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setLeads(parseLeads(data));
      setSelected([]);
      setReportRan(true);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const applyPreset = (preset) => {
    const { start, end } = preset.getValue();
    setStartDate(start);
    setEndDate(end);
    setDateAnchor(null);
  };

  const handleSelectAll = (e) => {
    const currentLeads = reportRan ? leads : pendingLeads;
    setSelected(e.target.checked ? currentLeads.map(l => l.id) : []);
  };
  
  const handleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const handleStatusChange = async (newStatus) => {
    if (selected.length === 0) return;
    try {
      await axios.put('/admin/leads/status', { lead_ids: selected, status: newStatus });
      setMessage({ type: 'success', text: `Updated ${selected.length} lead(s) to ${newStatus}` });
      if (reportRan) loadLeads();
      loadPendingLeads();
      setSelected([]);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to update status' });
    }
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir(orderDir === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDir('desc');
    }
  };

  const toggleColumn = (colId) => {
    setVisibleColumns(prev => prev.includes(colId) ? prev.filter(c => c !== colId) : [...prev, colId]);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const formatDateRange = () => {
    if (!startDate || !endDate) return 'Select dates';
    const s = startDate instanceof Date ? startDate : new Date(startDate);
    const e = endDate instanceof Date ? endDate : new Date(endDate);
    return s.toLocaleDateString() + ' - ' + e.toLocaleDateString();
  };

  const getAttribution = (lead) => {
    if (lead.metadata?.manual_import) return 'Manual';
    if (lead.tracking_type === 'pixel') return 'Pixel';
    if (lead.tracking_type === 'postback') return 'Postback';
    return 'Postback';
  };

  const filteredCampaigns = campaigns.filter(c => c.name?.toLowerCase().includes(searchCampaign.toLowerCase()) && (searchCampaign || c.status === 'active')).sort((a, b) => (b.status === 'active') - (a.status === 'active'));
  const filteredPartners = partners.filter(p => p.name?.toLowerCase().includes(searchPartner.toLowerCase()) && (searchPartner || p.status === 'active')).sort((a, b) => (b.status === 'active') - (a.status === 'active'));
  const filteredAdvertisers = advertisers.filter(a => a.name?.toLowerCase().includes(searchAdvertiser.toLowerCase()) && (searchAdvertiser || a.status === 'active')).sort((a, b) => (b.status === 'active') - (a.status === 'active'));

  const currentLeads = reportRan ? leads : pendingLeads;
  
  const sortedLeads = [...currentLeads].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (['created_at', 'click_date'].includes(orderBy)) {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    } else if (['revenue', 'payout'].includes(orderBy)) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else {
      aVal = (aVal || '').toString().toLowerCase();
      bVal = (bVal || '').toString().toLowerCase();
    }
    if (orderDir === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  const summary = {
    total: currentLeads.length,
    approved: currentLeads.filter(l => l.status === 'approved').length,
    pending: currentLeads.filter(l => l.status === 'pending').length,
    rejected: currentLeads.filter(l => l.status === 'rejected').length,
    invalid: currentLeads.filter(l => l.status === 'invalid').length,
   revenue: currentLeads.filter(l => l.status === 'approved' || l.status === 'pending').reduce((s, l) => s + (parseFloat(l.revenue) || parseFloat(l.event_revenue) || 0), 0),
    payout: currentLeads.filter(l => l.status === 'approved' || l.status === 'pending').reduce((s, l) => s + (parseFloat(l.payout) || parseFloat(l.event_payout) || 0), 0),
  };

  const columnGroups = [...new Set(allColumns.map(c => c.group))];

  const renderCellValue = (lead, colId) => {
    switch (colId) {
      case 'status':
        return (
          <Chip 
            label={lead.status} 
            color={statusColors[lead.status]} 
            size="small" 
            sx={{ fontSize: '0.75rem', ...(lead.status === 'approved' && { bgcolor: 'success.main', color: 'white' }), ...(lead.status === 'pending' && { bgcolor: 'warning.main', color: 'white' }) }} 
          />
        );
      case 'created_at':
      case 'click_date':
        return formatDate(lead[colId] || lead.created_at);
      case 'campaign_name':
        return (
          <Typography 
            sx={{ fontSize: '0.8rem', color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={(e) => { e.stopPropagation(); navigate(`/campaigns/${lead.campaign_id}`); }}
          >
            {lead.campaign_name || '-'}
          </Typography>
        );
      case 'partner_name':
        return (
          <Typography 
            sx={{ fontSize: '0.8rem', color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={(e) => { e.stopPropagation(); navigate(`/partners/${lead.partner_id}`); }}
          >
            {lead.partner_name || '-'}
          </Typography>
        );
      case 'revenue':
        if (isPartner()) return null;
        if (lead.status === 'rejected' || lead.status === 'invalid') return '€0.00';
       return `€${(parseFloat(lead.revenue) || parseFloat(lead.event_revenue) || 0).toFixed(2)}`;
      case 'payout':
        if (lead.status === 'rejected' || lead.status === 'invalid') return '€0.00';
        return `€${(parseFloat(lead.payout) || parseFloat(lead.event_payout) || 0).toFixed(2)}`;
      case 'conversion_ip':
        return <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{lead.conversion_ip || '-'}</span>;
      case 'session_ip':
        return <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{lead.session_ip || lead.click_ip || '-'}</span>;
      case 'click_id':
        return <span style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{lead.click_id || '-'}</span>;
      case 'user_agent':
        return <span style={{ fontSize: '0.7rem', maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.user_agent || lead.conversion_ua || '-'}</span>;
      case 'browser':
      case 'device':
      case 'os':
        return lead[colId] || '-';
      case 'city':
        return lead.city || '-';
      case 'source_id':
        return lead.source_id || lead.click_source || '-';
      case 'creative_id':
        return lead.creative_id || '-';
      case 'offer_url_id':
        return lead.offer_url_id || '-';
      case 'event_name':
        return lead.event_name || 'CPL';
      case 'attribution':
        return <Chip label={getAttribution(lead)} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
      default:
        return lead[colId] || '-';
    }
  };

  const goToDetails = (leadId) => {
    updateUrlParams();
    navigate(`/conversions/${leadId}`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h5" fontWeight={700} mb={3}>Conversions</Typography>
        {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}
        
        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Date Picker */}
              <Button variant="outlined" startIcon={<CalendarIcon />} onClick={(e) => setDateAnchor(e.currentTarget)} size="medium" sx={{ minWidth: 180 }}>
                {formatDateRange()}
              </Button>
              <Popover open={Boolean(dateAnchor)} anchorEl={dateAnchor} onClose={() => setDateAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Box sx={{ p: 2, minWidth: 200 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Presets</Typography>
                  <List dense>{datePresets.map(p => <ListItemButton key={p.label} onClick={() => applyPreset(p)}>{p.label}</ListItemButton>)}</List>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <DatePicker label="Start" value={startDate} onChange={(date) => setStartDate(date)} slotProps={{ textField: { size: 'small' } }} />
                    <DatePicker label="End" value={endDate} onChange={(date) => setEndDate(date)} slotProps={{ textField: { size: 'small' } }} />
                  </Box>
                </Box>
              </Popover>

              {/* Columns */}
              <Button variant="outlined" startIcon={<ColumnsIcon />} onClick={(e) => setColAnchor(e.currentTarget)} size="medium">
                Columns
              </Button>
              <Popover open={Boolean(colAnchor)} anchorEl={colAnchor} onClose={() => setColAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Box sx={{ p: 2, minWidth: 280, maxHeight: 400, overflow: 'auto' }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Show/Hide Columns</Typography>
                  {columnGroups.map(group => (
                    <Box key={group} mb={2}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>{group}</Typography>
                      {allColumns.filter(c => c.group === group).map(col => (
                        <ListItemButton key={col.id} onClick={() => toggleColumn(col.id)} dense sx={{ py: 0.5 }}>
                          <Checkbox checked={visibleColumns.includes(col.id)} size="small" />
                          <ListItemText primary={col.label} />
                        </ListItemButton>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Popover>

              {!isMobile && (
                <>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Campaign</InputLabel>
                    <Select multiple value={filters.campaigns} onChange={(e) => setFilters({...filters, campaigns: e.target.value})} input={<OutlinedInput label="Campaign" />} renderValue={(sel) => sel.length + ' sel.'} MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}>
                      <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }}>
                        <TextField size="small" fullWidth placeholder="Search..." value={searchCampaign} onChange={(e) => setSearchCampaign(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} />
                      </Box>
                      {filteredCampaigns.map(c => <MenuItem key={c.campaign_id} value={c.campaign_id}><Checkbox checked={filters.campaigns.includes(c.campaign_id)} /><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.status === 'active' ? 'success.main' : 'grey.400', mr: 1 }} /><ListItemText primary={`${c.name} (${c.campaign_id})`} /></MenuItem>)}
                    </Select>
                  </FormControl>

                  {!isPartner() && (
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Partner</InputLabel>
                    <Select multiple value={filters.partners} onChange={(e) => setFilters({...filters, partners: e.target.value})} input={<OutlinedInput label="Partner" />} renderValue={(sel) => sel.length + ' sel.'} MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}>
                      <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }}>
                        <TextField size="small" fullWidth placeholder="Search..." value={searchPartner} onChange={(e) => setSearchPartner(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} />
                      </Box>
                      {filteredPartners.map(p => <MenuItem key={p.partner_id} value={p.partner_id}><Checkbox checked={filters.partners.includes(p.partner_id)} /><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.status === 'active' ? 'success.main' : 'grey.400', mr: 1 }} /><ListItemText primary={`${p.name} (${p.partner_id})`} /></MenuItem>)}
                    </Select>
                  </FormControl>
                  )}

                   {!isPartner() && (
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Advertiser</InputLabel>
                    <Select multiple value={filters.advertisers} onChange={(e) => setFilters({...filters, advertisers: e.target.value})} input={<OutlinedInput label="Advertiser" />} renderValue={(sel) => sel.length + ' sel.'} MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}>
                      <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }}>
                        <TextField size="small" fullWidth placeholder="Search..." value={searchAdvertiser} onChange={(e) => setSearchAdvertiser(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} />
                      </Box>
                      {filteredAdvertisers.map(a => <MenuItem key={a.advertiser_id} value={a.advertiser_id}><Checkbox checked={filters.advertisers.includes(a.advertiser_id)} /><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: a.status === 'active' ? 'success.main' : 'grey.400', mr: 1 }} /><ListItemText primary={`${a.name} (${a.advertiser_id})`} /></MenuItem>)}
                    </Select>
                  </FormControl>
                 )}
                </>
              )}

                <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                {!isPartner() && (
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})} label="Status">
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="invalid">Invalid</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={loadLeads}>
                  Run Report
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Summary */}
        {currentLeads.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>Summary</Typography>
               <Box sx={{ display: 'grid', gridTemplateColumns: isPartner() ? 'repeat(3, 1fr)' : 'repeat(7, 1fr)', gap: 2 }}>
                <Box><Typography variant="caption" color="text.secondary">Total</Typography><Typography variant="h6">{summary.total}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Approved</Typography><Typography variant="h6" color="success.main">{summary.approved}</Typography></Box>
                {!isPartner() && <Box><Typography variant="caption" color="text.secondary">Pending</Typography><Typography variant="h6" color="warning.main">{summary.pending}</Typography></Box>}
                {!isPartner() && <Box><Typography variant="caption" color="text.secondary">Rejected</Typography><Typography variant="h6" color="error.main">{summary.rejected}</Typography></Box>}
                {!isPartner() && <Box><Typography variant="caption" color="text.secondary">Invalid</Typography><Typography variant="h6">{summary.invalid}</Typography></Box>}
                {!isPartner() && <Box><Typography variant="caption" color="text.secondary">Revenue</Typography><Typography variant="h6">€{summary.revenue.toFixed(2)}</Typography></Box>}
                <Box><Typography variant="caption" color="text.secondary">Payout</Typography><Typography variant="h6">€{summary.payout.toFixed(2)}</Typography></Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Bulk Actions */}
          {selected.length > 0 && !isPartner() && (
          <Card sx={{ mb: 2, bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography>{selected.length} selected</Typography>
              <Button size="small" variant="contained" color="success" sx={{ color: 'white' }} onClick={() => handleStatusChange('approved')}>Approve</Button>
              <Button size="small" variant="contained" color="warning" sx={{ color: 'white' }} onClick={() => handleStatusChange('pending')}>Pending</Button>
              <Button size="small" variant="contained" color="error" onClick={() => handleStatusChange('rejected')}>Reject</Button>
              <Button size="small" variant="outlined" onClick={() => handleStatusChange('invalid')}>Invalid</Button>
              <Button size="small" onClick={() => setSelected([])}>Clear</Button>
            </CardContent>
          </Card>
        )}

        {/* Title based on view */}
        {!reportRan && pendingLeads.length > 0 && (
          <Typography variant="subtitle1" fontWeight={600} mb={2} color="warning.main">
            Pending Leads ({pendingLeads.length})
          </Typography>
        )}

        {/* Table */}
        <Card>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox checked={currentLeads.length > 0 && selected.length === currentLeads.length} onChange={handleSelectAll} />
                  </TableCell>
                                        {allColumns.filter(c => visibleColumns.includes(c.id) && !(isPartner() && ['revenue', 'partner_name', 'attribution'].includes(c.id))).map(col => (
                    <TableCell key={col.id} sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                      {['created_at', 'click_date', 'revenue', 'payout'].includes(col.id) ? (
                        <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? orderDir : 'asc'} onClick={() => handleSort(col.id)}>
                          {col.label}
                        </TableSortLabel>
                      ) : col.label}
                    </TableCell>
                  ))}

                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={visibleColumns.length + 2} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
                ) : currentLeads.length === 0 ? (
                  <TableRow><TableCell colSpan={visibleColumns.length + 2} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {reportRan ? 'No leads found for selected criteria' : 'No pending leads. Click "Run Report" to load approved leads.'}
                  </TableCell></TableRow>
                ) : sortedLeads.map((lead) => (
                  <TableRow key={lead.id} hover selected={selected.includes(lead.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.includes(lead.id)} onChange={() => handleSelect(lead.id)} />
                    </TableCell>
                                           {allColumns.filter(c => visibleColumns.includes(c.id) && !(isPartner() && ['revenue', 'partner_name', 'attribution'].includes(c.id))).map(col => (
                      <TableCell key={col.id} sx={{ fontSize: '0.8rem' }}>
                        {renderCellValue(lead, col.id)}
                      </TableCell>
                    ))}

                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </LocalizationProvider>
  );
}

export default Conversions;
